import { defineStore } from 'pinia';
import VueJwtDecode from 'vue-jwt-decode';
import OauthService from '@/services/oauth.service';
import consts from '@/consts';
import { useUserStore } from '@/store/user.module';

const validLocales = [
  'en',
  'fr',
  'es',
];

const navigatorLocale = navigator.language.split('-')[0];
const defaultLocale = 'en';

const locale = validLocales.includes(navigatorLocale) ? navigatorLocale : defaultLocale;

function hasRole(jwt, role) {
  try {
    const decodedToken = VueJwtDecode.decode(jwt);
    return decodedToken.roles.includes(role);
  } catch (e) {
    return false;
  }
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    jwt: null,
    locale,
    cookieAccepted: false,
    userUpdatedAt: new Date(),
    debug: false,
  }),
  getters: {
    isLoggedIn: (state) => state.jwt !== null,
    hasRoleBeta: (state) => hasRole(state.jwt, consts.ROLE.BETA),
    hasRoleAdmin: (state) => hasRole(state.jwt, consts.ROLE.ADMIN),
    getLocale: (state) => state.locale,
    isCookieAccepted: (state) => state.cookieAccepted,
    getUserUpdatedAt: (state) => state.userUpdatedAt,
  },
  actions: {
    async getAccessToken(authorizationCode) {
      try {
        if (this.debug) {
          return Promise.reject(new Error('Debug mode is enabled.'));
        }

        const response = await OauthService.getAccessToken(authorizationCode);
        this.$patch({
          jwt: response.jwt,
          userUpdatedAt: new Date(),
        });
        const userStore = useUserStore();
        userStore.setCurrentUser(response.user);
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async refreshCurrentUser() {
      try {
        const response = await OauthService.refreshCurrentUser();
        this.$patch({
          jwt: response.jwt,
          userUpdatedAt: new Date(),
        });
        const userStore = useUserStore();
        userStore.setCurrentUser(response.user);
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    setLocale(data) {
      this.locale = data;
    },
    async logout() {
      this.jwt = null;
      const userStore = useUserStore();
      userStore.clearUser();
    },
    acceptCookie() {
      this.cookieAccepted = true;
      localStorage.setItem('cookieAccepted', 'true');
    },
    declineCookie() {
      this.cookieAccepted = false;
      localStorage.setItem('cookieAccepted', 'false');
    },
  },
  persist: {
    serializer: {
      serialize: (state) => JSON.stringify(state),
      deserialize: (data) => {
        const parsed = JSON.parse(data)
        if (parsed.userUpdatedAt) {
          parsed.userUpdatedAt = new Date(parsed.userUpdatedAt);
        }

        parsed.cookieAccepted = Boolean(parsed.cookieAccepted);

        return parsed
      },
    },
  },
});
